import { useClerk, useSignIn, useSignUp } from '@clerk/react';
import { lazy, Suspense, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PasswordResetReason } from '@/components/auth/PasswordResetPanel';
import { AppleMark, GoogleMark } from '@/components/auth/ProviderMarks';
import {
  inputClassName,
  labelClassName,
  primaryButtonClassName,
} from '@/components/auth/signInFormClasses';
import { LoadingIcon } from '@/components/icons/line/loading';
import { resolveEmailCodeAuthStep } from '@/lib/auth/email-code-auth-flow';
import { hasClerkErrorCode } from '@/lib/auth/clerk-error-code';
import { finalizeClerkSignIn } from '@/lib/auth/finalize-clerk-sign-in';
import { finalizeClerkSignUp } from '@/lib/auth/finalize-clerk-sign-up';
import { signUpCompletionUrl } from '@/lib/auth/sign-up-completion-url';
import {
  isIdentifierNotFoundError,
  isPasswordAuthUnavailableError,
  isPasswordBreachedError,
  isSessionExistsError,
} from '@/lib/auth/login-session';
import {
  isOAuthPendingMethod,
  isOAuthUserCancelledError,
  OAUTH_PENDING_FOCUS_RECOVERY_MS,
  OAUTH_PENDING_STALL_MS,
} from '@/lib/auth/oauth-sign-in-pending';
import { validateSignInEmailForCode } from '@/lib/auth/validate-sign-in-email';
import {
  prepareDeviceTrustVerification,
  resolvePasswordSignInStep,
} from '@/lib/auth/password-sign-in-flow';

type OAuthSignInStrategy = 'oauth_google' | 'oauth_apple';
type AlternateMethod = 'google' | 'apple';
type AuthMode = 'sign_in' | 'sign_up';
type FirstFactorMethod = 'password' | 'email_code';

const PASSWORD_DISABLED_HINT =
  'Password sign-in is not available for this account. Use Email me a code instead.';

const loadPasswordResetPanel = () => import('@/components/auth/PasswordResetPanel');
const PasswordResetPanel = lazy(() =>
  loadPasswordResetPanel().then((module) => ({ default: module.PasswordResetPanel })),
);

const alternateButtonClassName =
  'flex h-11 min-w-0 items-center justify-center gap-1.5 rounded-full border border-black/15 bg-white px-1.5 text-sm font-bold text-ink transition hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-60 sm:gap-2 sm:px-2';

const secondaryLinkClassName = 'font-bold text-coral hover:underline';

export type AuthSignInCardProps = {
  signInRedirectUrl: string;
  signUpRedirectUrl: string;
  authNextQuery: string;
  lockedInviteEmail?: string | null;
  inviteTripTitle?: string | null;
  autoSendInviteCode?: boolean;
};

export function AuthSignInCard({
  signInRedirectUrl,
  signUpRedirectUrl,
  authNextQuery,
  lockedInviteEmail = null,
  inviteTripTitle = null,
  autoSendInviteCode = false,
}: AuthSignInCardProps) {
  const clerk = useClerk();
  const navigate = useNavigate();
  const { signIn, errors, fetchStatus } = useSignIn();
  const { signUp, errors: signUpErrors, fetchStatus: signUpFetchStatus } = useSignUp();
  const formId = useId();
  const deviceTrustPreparedRef = useRef(false);
  const inviteCodeSentRef = useRef(false);
  const oauthRecoveryTimerRef = useRef<number | null>(null);

  const [identifier, setIdentifier] = useState(lockedInviteEmail ?? '');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [inlineIdentifierError, setInlineIdentifierError] = useState<string | null>(null);
  const [pendingMethod, setPendingMethod] = useState<AlternateMethod | null>(null);
  const [resetReason, setResetReason] = useState<PasswordResetReason | null>(null);
  const [firstFactorMethod, setFirstFactorMethod] = useState<FirstFactorMethod>(
    autoSendInviteCode ? 'email_code' : 'password',
  );
  const [authMode, setAuthMode] = useState<AuthMode>('sign_in');
  const [codeStepActive, setCodeStepActive] = useState(false);
  const [mfaStep, setMfaStep] = useState<'totp' | 'mfa_email_code' | null>(null);

  // A restored, verified signup may still require a password or other fields.
  // Clerk owns those requirements; resume its completion UI instead of asking
  // the traveler to verify an already-verified email again.
  const needsVerifiedSignUpCompletion = Boolean(
    signUp?.status === 'missing_requirements' &&
    signUp.emailAddress &&
    signUp.missingFields.length > 0 &&
    !signUp.unverifiedFields.includes('email_address') &&
    (!lockedInviteEmail ||
      normalizeEmail(signUp.emailAddress) === normalizeEmail(lockedInviteEmail)),
  );
  const signUpCompletionHref = signUpCompletionUrl(
    authNextQuery,
    signUp?.missingFields ?? [],
    signUp?.unverifiedFields ?? [],
  );
  useEffect(() => {
    if (needsVerifiedSignUpCompletion) {
      navigate(signUpCompletionHref, { replace: true });
    }
  }, [needsVerifiedSignUpCompletion, signUpCompletionHref, navigate]);

  const passwordStep = signIn
    ? resolvePasswordSignInStep(signIn.status, signIn.supportedSecondFactors)
    : 'credentials';
  const isSubmitting = fetchStatus === 'fetching' || signUpFetchStatus === 'fetching';
  const isBusy = isSubmitting || pendingMethod !== null;
  const emailLocked = Boolean(lockedInviteEmail?.trim());

  useEffect(() => {
    if (lockedInviteEmail) {
      setIdentifier(lockedInviteEmail);
    }
  }, [lockedInviteEmail]);

  useEffect(() => {
    if (!isOAuthPendingMethod(pendingMethod)) return;
    const timer = window.setTimeout(() => setPendingMethod(null), OAUTH_PENDING_STALL_MS);
    return () => window.clearTimeout(timer);
  }, [pendingMethod]);

  useEffect(() => {
    if (!isOAuthPendingMethod(pendingMethod)) {
      return;
    }

    const scheduleRecovery = () => {
      if (document.visibilityState === 'hidden') return;
      if (oauthRecoveryTimerRef.current !== null) {
        window.clearTimeout(oauthRecoveryTimerRef.current);
      }
      oauthRecoveryTimerRef.current = window.setTimeout(() => {
        oauthRecoveryTimerRef.current = null;
        setPendingMethod((current) => (isOAuthPendingMethod(current) ? null : current));
      }, OAUTH_PENDING_FOCUS_RECOVERY_MS);
    };

    window.addEventListener('focus', scheduleRecovery);
    document.addEventListener('visibilitychange', scheduleRecovery);

    return () => {
      window.removeEventListener('focus', scheduleRecovery);
      document.removeEventListener('visibilitychange', scheduleRecovery);
      if (oauthRecoveryTimerRef.current !== null) {
        window.clearTimeout(oauthRecoveryTimerRef.current);
        oauthRecoveryTimerRef.current = null;
      }
    };
  }, [pendingMethod]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setPendingMethod(null);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    if (!signIn || passwordStep !== 'device_trust' || deviceTrustPreparedRef.current) {
      return;
    }
    deviceTrustPreparedRef.current = true;
    void prepareDeviceTrustVerification(signIn).catch(() => {
      deviceTrustPreparedRef.current = false;
    });
  }, [signIn, passwordStep]);

  useEffect(() => {
    if (passwordStep === 'credentials') {
      deviceTrustPreparedRef.current = false;
    }
  }, [passwordStep]);

  useEffect(() => {
    if (!autoSendInviteCode || !lockedInviteEmail || inviteCodeSentRef.current) return;
    if (!clerk.loaded || !signIn || !signUp) return;
    inviteCodeSentRef.current = true;
    setFirstFactorMethod('email_code');
    const email = lockedInviteEmail.trim();
    void (async () => {
      setFormError(null);
      const { error } = await signIn.emailCode.sendCode({ emailAddress: email });
      if (error) {
        inviteCodeSentRef.current = false;
        if (
          isIdentifierNotFoundError(error) ||
          hasClerkErrorCode(error, ['form_identifier_not_found', 'factor_not_found'])
        ) {
          const { error: createError } = await signUp.create({ emailAddress: email });
          if (createError) {
            setFormError(createError.message || 'We could not start sign-up for that email.');
            return;
          }
          const { error: sendError } = await signUp.verifications.sendEmailCode();
          if (sendError) {
            setFormError(sendError.message || 'We could not send a sign-in code to that email.');
            return;
          }
          setAuthMode('sign_up');
          setIdentifier(email);
          setCodeStepActive(true);
          return;
        }
        setFormError(error.message || 'We could not send a sign-in code to that email.');
        return;
      }
      setAuthMode('sign_in');
      setIdentifier(email);
      setCodeStepActive(true);
    })();
  }, [autoSendInviteCode, lockedInviteEmail, clerk.loaded, signIn, signUp]);

  if (!clerk.loaded || !signIn || !signUp) {
    return null;
  }

  const navigateAfterAuth = (path: string) => {
    if (path.startsWith('http')) {
      window.location.href = path;
      return;
    }
    navigate(path, { replace: true });
  };

  const reportError = (error: { message?: string }, fallback: string) => {
    if (isSessionExistsError(error)) {
      window.location.replace(signInRedirectUrl);
      return;
    }
    setPendingMethod(null);
    if (!resetReason && isPasswordBreachedError(error)) {
      openPasswordReset('breached');
      return;
    }
    if (isPasswordAuthUnavailableError(error)) {
      setFormError(PASSWORD_DISABLED_HINT);
      setFirstFactorMethod('email_code');
      return;
    }
    setFormError(error.message || fallback);
  };

  const openPasswordReset = (reason: PasswordResetReason) => {
    setFormError(null);
    setResetReason(reason);
  };

  const closePasswordReset = async () => {
    setFormError(null);
    setResetReason(null);
    await signIn.reset();
  };

  const completeSignIn = async () => {
    if (signIn.status !== 'complete') {
      setPendingMethod(null);
      setFormError('Sign-in could not be finished. Try again.');
      return;
    }
    setFormError(null);
    await finalizeClerkSignIn(signIn, signInRedirectUrl, navigateAfterAuth);
  };

  const completeSignUp = async () => {
    if (signUp.status !== 'complete') {
      if (signUp.status === 'missing_requirements') {
        navigate(
          signUpCompletionUrl(authNextQuery, signUp.missingFields, signUp.unverifiedFields),
          { replace: true },
        );
        return;
      }
      setPendingMethod(null);
      setFormError('Account setup could not be finished. Try again.');
      return;
    }
    setFormError(null);
    await finalizeClerkSignUp(signUp, signUpRedirectUrl, navigateAfterAuth);
  };

  const startSignUpForEmail = async (email: string) => {
    const { error: createError } = await signUp.create({ emailAddress: email });
    if (createError) {
      reportError(createError, 'We could not start sign-up for that email.');
      return false;
    }
    const { error: sendError } = await signUp.verifications.sendEmailCode();
    if (sendError) {
      reportError(sendError, 'We could not send a sign-in code to that email.');
      return false;
    }
    setAuthMode('sign_up');
    setCodeStepActive(true);
    setFormError(null);
    setInlineIdentifierError(null);
    return true;
  };

  const sendEmailCode = async (emailAddress: string) => {
    setFormError(null);
    const email = emailAddress.trim();
    if (!email) {
      setFormError('Enter your email address.');
      return;
    }
    if (
      emailLocked &&
      lockedInviteEmail &&
      normalizeEmail(email) !== normalizeEmail(lockedInviteEmail)
    ) {
      setFormError('Sign in with the email address this trip invite was sent to.');
      return;
    }

    const { error } = await signIn.emailCode.sendCode({ emailAddress: email });
    if (error) {
      if (
        isIdentifierNotFoundError(error) ||
        hasClerkErrorCode(error, ['form_identifier_not_found', 'factor_not_found'])
      ) {
        await startSignUpForEmail(email);
        return;
      }
      reportError(error, 'We could not send a sign-in code to that email.');
      return;
    }

    setAuthMode('sign_in');
    setCodeStepActive(true);
    setMfaStep(null);
  };

  const continueSignIn = async () => {
    if (signIn.status === 'complete') {
      await completeSignIn();
      return;
    }

    if (
      resolvePasswordSignInStep(signIn.status, signIn.supportedSecondFactors) === 'device_trust'
    ) {
      return;
    }

    const followUp = resolveEmailCodeAuthStep(signIn.status, signIn.supportedSecondFactors);
    if (followUp === 'totp') {
      setMfaStep('totp');
      return;
    }
    if (followUp === 'mfa_email_code') {
      const { error } = await signIn.mfa.sendEmailCode();
      if (error) {
        reportError(error, 'We could not send your verification code.');
        return;
      }
      setMfaStep('mfa_email_code');
      return;
    }

    if (signIn.status === 'needs_second_factor') {
      setFormError(
        "Your account needs a verification step this sign-in page doesn't support yet. Try Google or Apple, or finish setup in Account settings.",
      );
      return;
    }

    setFormError('Sign-in could not be finished. Try again or use another sign-in method.');
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const { error } = await signIn.password({
      emailAddress: identifier.trim(),
      password,
    });

    if (error) {
      if (isPasswordAuthUnavailableError(error)) {
        reportError(error, PASSWORD_DISABLED_HINT);
        return;
      }
      if (isIdentifierNotFoundError(error)) {
        const { error: signUpError } = await signUp.password({
          emailAddress: identifier.trim(),
          password,
        });
        if (signUpError) {
          if (isPasswordAuthUnavailableError(signUpError)) {
            reportError(signUpError, PASSWORD_DISABLED_HINT);
            return;
          }
          reportError(signUpError, 'Unable to create an account with that email and password.');
          return;
        }
        await completeSignUp();
        return;
      }
      reportError(error, 'Unable to sign in with that email and password.');
      return;
    }

    await continueSignIn();
  };

  const handlePasswordReset = async () => {
    if (signIn.status !== 'complete') {
      setResetReason(null);
    }
    await continueSignIn();
  };

  const handleDeviceTrustSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const { error } = await signIn.mfa.verifyEmailCode({ code: verificationCode.trim() });
    if (error) {
      reportError(error, 'That code did not work. Try again or request a new code.');
      return;
    }

    await completeSignIn();
  };

  const handleEmailCodeRequestSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await sendEmailCode(identifier);
  };

  const handleEmailCodeSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const code = verificationCode.trim();

    if (authMode === 'sign_up') {
      const { error } = await signUp.verifications.verifyEmailCode({ code });
      if (error) {
        reportError(error, 'That code did not work. Try again or request a new one.');
        return;
      }
      await completeSignUp();
      return;
    }

    const { error } = await signIn.emailCode.verifyCode({ code });
    if (error) {
      reportError(error, 'That code did not work. Try again or request a new one.');
      return;
    }
    await continueSignIn();
  };

  const handleTotpSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const { error } = await signIn.mfa.verifyTOTP({ code: totpCode.trim() });
    if (error) {
      reportError(error, 'That authenticator code did not work. Try again.');
      return;
    }
    await completeSignIn();
  };

  const handleMfaEmailCodeSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const { error } = await signIn.mfa.verifyEmailCode({ code: verificationCode.trim() });
    if (error) {
      reportError(error, 'That code did not work. Try again or request a new one.');
      return;
    }
    await completeSignIn();
  };

  const handleOAuth = async (strategy: OAuthSignInStrategy) => {
    setFormError(null);
    setPendingMethod(strategy === 'oauth_google' ? 'google' : 'apple');
    try {
      const { error } = await signIn.sso({
        strategy,
        redirectCallbackUrl: `/login/sso-callback${authNextQuery}`,
        redirectUrl: signInRedirectUrl,
      });
      if (error) {
        if (isOAuthUserCancelledError(error)) {
          setPendingMethod(null);
          return;
        }
        reportError(error, 'Unable to start sign-in with that provider.');
      }
    } catch (caught) {
      if (isOAuthUserCancelledError(caught)) {
        setPendingMethod(null);
        return;
      }
      reportError(
        caught instanceof Error ? caught : { message: String(caught) },
        'Unable to start sign-in with that provider.',
      );
    }
  };

  const handleResendCode = async () => {
    setFormError(null);
    if (authMode === 'sign_up') {
      const { error } = await signUp.verifications.sendEmailCode();
      if (error) {
        reportError(error, 'Could not send a new code. Try again in a moment.');
      }
      return;
    }
    if (mfaStep === 'mfa_email_code') {
      const { error } = await signIn.mfa.sendEmailCode();
      if (error) {
        reportError(error, 'Could not send a new code. Try again in a moment.');
      }
      return;
    }
    if (passwordStep === 'device_trust') {
      const { error } = await signIn.mfa.sendEmailCode();
      if (error) {
        reportError(error, 'Could not send a new code. Try again in a moment.');
      }
      return;
    }
    await sendEmailCode(identifier);
  };

  const handleStartOver = async () => {
    setFormError(null);
    setInlineIdentifierError(null);
    setVerificationCode('');
    setTotpCode('');
    setCodeStepActive(false);
    setMfaStep(null);
    setAuthMode('sign_in');
    inviteCodeSentRef.current = false;
    await signIn.reset();
  };

  const requestInlineEmailCode = async () => {
    setFormError(null);
    setResetReason(null);
    setMfaStep(null);
    const validationError = validateSignInEmailForCode(identifier, { lockedInviteEmail });
    if (validationError) {
      setInlineIdentifierError(validationError);
      return;
    }
    setInlineIdentifierError(null);
    await sendEmailCode(identifier);
  };

  const switchBackToPasswordFromInlineCode = () => {
    setFormError(null);
    setInlineIdentifierError(null);
    setVerificationCode('');
    setCodeStepActive(false);
    setMfaStep(null);
    setAuthMode('sign_in');
    void signIn.reset();
  };

  const switchToPassword = () => {
    setFormError(null);
    setInlineIdentifierError(null);
    setFirstFactorMethod('password');
    setCodeStepActive(false);
    setMfaStep(null);
    void signIn.reset();
  };

  const showDedicatedEmailCodeStep =
    firstFactorMethod === 'email_code' && codeStepActive && !mfaStep;
  const showInlineEmailCodeOnPasswordForm =
    firstFactorMethod === 'password' && codeStepActive && !mfaStep;
  const showTotpStep = mfaStep === 'totp';
  const showMfaEmailStep = mfaStep === 'mfa_email_code';
  const signInIdentifierFieldMessage = codeStepActive
    ? null
    : (errors.fields.identifier?.message ?? null);

  return (
    <div className="w-full overflow-hidden rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
      <div className="grid grid-cols-2 gap-2">
        <AlternateMethodButton
          name="Continue with Google"
          label="Google"
          mark={<GoogleMark className="h-[18px] w-[18px] shrink-0" />}
          pending={pendingMethod === 'google'}
          disabled={isBusy}
          onClick={() => void handleOAuth('oauth_google')}
        />
        <AlternateMethodButton
          name="Continue with Apple"
          label="Apple"
          mark={<AppleMark className="h-[18px] w-[18px] shrink-0" />}
          pending={pendingMethod === 'apple'}
          disabled={isBusy}
          onClick={() => void handleOAuth('oauth_apple')}
        />
      </div>

      <div className="my-4 flex items-center gap-3">
        <span className="h-px flex-1 bg-black/10" />
        <span className="text-xs font-bold uppercase tracking-wide text-ink/55">or</span>
        <span className="h-px flex-1 bg-black/10" />
      </div>

      {inviteTripTitle && emailLocked ? (
        <p className="mb-3 text-sm leading-6 text-black/60">
          Sign in to join <span className="font-semibold text-ink">{inviteTripTitle}</span>. Use the
          email and password for this invite, or we can email you a one-time code.
        </p>
      ) : null}

      {showTotpStep ? (
        <form onSubmit={handleTotpSubmit} className="space-y-4" aria-labelledby={`${formId}-totp`}>
          <div>
            <p id={`${formId}-totp`} className="text-sm font-semibold text-ink">
              Authenticator code
            </p>
            <p className="mt-1 text-sm text-black/55">
              Enter the 6-digit code from your authenticator app.
            </p>
          </div>
          <div className="space-y-2">
            <label htmlFor={`${formId}-totp-code`} className={labelClassName}>
              Code
            </label>
            <input
              id={`${formId}-totp-code`}
              name="totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value)}
              className={inputClassName}
            />
          </div>
          <button type="submit" className={primaryButtonClassName} disabled={isBusy}>
            Continue
          </button>
        </form>
      ) : showMfaEmailStep ? (
        <form
          onSubmit={handleMfaEmailCodeSubmit}
          className="space-y-4"
          aria-labelledby={`${formId}-mfa-email`}
        >
          <div>
            <p id={`${formId}-mfa-email`} className="text-sm font-semibold text-ink">
              Check your email
            </p>
            <p className="mt-1 text-sm text-black/55">
              We sent a verification code to{' '}
              <span className="font-semibold text-ink">{identifier}</span>.
            </p>
          </div>
          <div className="space-y-2">
            <label htmlFor={`${formId}-mfa-code`} className={labelClassName}>
              Verification code
            </label>
            <input
              id={`${formId}-mfa-code`}
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value)}
              className={inputClassName}
            />
            {errors.fields.code?.message ? (
              <p className="text-sm font-medium text-red-600">{errors.fields.code.message}</p>
            ) : null}
          </div>
          <button type="submit" className={primaryButtonClassName} disabled={isBusy}>
            Continue
          </button>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <button
              type="button"
              className={secondaryLinkClassName}
              onClick={() => void handleResendCode()}
              disabled={isSubmitting}
            >
              Didn&apos;t get a code? Send again
            </button>
          </div>
        </form>
      ) : passwordStep === 'device_trust' ? (
        <form
          onSubmit={handleDeviceTrustSubmit}
          className="space-y-4"
          aria-labelledby={`${formId}-trust-title`}
        >
          <div>
            <p id={`${formId}-trust-title`} className="text-sm font-semibold text-ink">
              Verify it&apos;s you
            </p>
            <p className="mt-1 text-sm text-black/55">
              We sent a 6-digit code to <span className="font-semibold text-ink">{identifier}</span>
              .
            </p>
          </div>

          <div
            className="rounded-xl border border-[#F5A623]/40 bg-[#FFF4E5] px-4 py-3 text-sm leading-6 text-[#8A4B00]"
            role="status"
          >
            You&apos;re signing in from a new device. We&apos;re asking for verification to keep
            your account secure.
          </div>

          <div className="space-y-2">
            <label htmlFor={`${formId}-code`} className={labelClassName}>
              Verification code
            </label>
            <input
              id={`${formId}-code`}
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value)}
              className={inputClassName}
            />
            {errors.fields.code?.message ? (
              <p className="text-sm font-medium text-red-600">{errors.fields.code.message}</p>
            ) : null}
          </div>

          <button type="submit" className={primaryButtonClassName} disabled={isBusy}>
            Continue
          </button>

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <button
              type="button"
              className={secondaryLinkClassName}
              onClick={() => void handleResendCode()}
              disabled={isSubmitting}
            >
              Didn&apos;t receive a code? Resend
            </button>
            {!emailLocked ? (
              <button
                type="button"
                className="font-semibold text-black/55 hover:text-ink"
                onClick={() => void handleStartOver()}
                disabled={isSubmitting}
              >
                Use a different email
              </button>
            ) : null}
          </div>
        </form>
      ) : resetReason ? (
        <Suspense fallback={<div className="h-56 animate-pulse rounded-xl bg-black/[0.04]" />}>
          <PasswordResetPanel
            signIn={signIn}
            reason={resetReason}
            email={identifier}
            onEmailChange={setIdentifier}
            busy={isBusy}
            onError={reportError}
            onClearError={() => setFormError(null)}
            onPasswordReset={handlePasswordReset}
            onCancel={() => void closePasswordReset()}
          />
        </Suspense>
      ) : showDedicatedEmailCodeStep ? (
        <form
          onSubmit={handleEmailCodeSubmit}
          className="space-y-4"
          aria-labelledby={`${formId}-code-title`}
        >
          <div>
            <p id={`${formId}-code-title`} className="text-sm font-semibold text-ink">
              Check your email
            </p>
            <p className="mt-1 text-sm text-black/55">
              {authMode === 'sign_up' ? (
                <>
                  We sent a code to <span className="font-semibold text-ink">{identifier}</span> to
                  create your account.
                </>
              ) : (
                <>
                  We sent a sign-in code to{' '}
                  <span className="font-semibold text-ink">{identifier}</span>.
                </>
              )}
            </p>
          </div>
          <div className="space-y-2">
            <label htmlFor={`${formId}-code`} className={labelClassName}>
              Sign-in code
            </label>
            <input
              id={`${formId}-code`}
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value)}
              className={inputClassName}
            />
            {errors.fields.code?.message ? (
              <p className="text-sm font-medium text-red-600">{errors.fields.code.message}</p>
            ) : null}
          </div>
          <button type="submit" className={primaryButtonClassName} disabled={isBusy}>
            Continue
          </button>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <button
              type="button"
              className={secondaryLinkClassName}
              onClick={() => void handleResendCode()}
              disabled={isSubmitting}
            >
              Didn&apos;t get a code? Send again
            </button>
            {!emailLocked ? (
              <button
                type="button"
                className="font-semibold text-black/55 hover:text-ink"
                onClick={() => void handleStartOver()}
                disabled={isSubmitting}
              >
                Use a different email
              </button>
            ) : null}
          </div>
        </form>
      ) : firstFactorMethod === 'email_code' ? (
        <form
          onSubmit={handleEmailCodeRequestSubmit}
          className="space-y-4"
          aria-labelledby={`${formId}-email-code-title`}
        >
          <p id={`${formId}-email-code-title`} className="text-sm text-black/55">
            {emailLocked
              ? 'We’ll email a one-time code to this address. New here? We’ll create your account after you enter the code.'
              : 'Enter your email and we’ll send a one-time code. New here? We’ll create your account after you enter the code.'}
          </p>
          <div className="space-y-2">
            <label htmlFor={`${formId}-email`} className={labelClassName}>
              Email address
            </label>
            <input
              id={`${formId}-email`}
              name="email"
              type="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              readOnly={emailLocked}
              aria-readonly={emailLocked || undefined}
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              className={`${inputClassName}${emailLocked ? ' bg-black/[0.04] text-black/70' : ''}`}
            />
            {signInIdentifierFieldMessage ? (
              <p className="text-sm font-medium text-red-600">{signInIdentifierFieldMessage}</p>
            ) : null}
          </div>
          <button type="submit" className={primaryButtonClassName} disabled={isBusy}>
            Email me a code
          </button>
          {!emailLocked ? (
            <p className="text-center text-sm">
              <button type="button" className={secondaryLinkClassName} onClick={switchToPassword}>
                Use email and password instead
              </button>
            </p>
          ) : null}
        </form>
      ) : (
        <form
          onSubmit={
            showInlineEmailCodeOnPasswordForm ? handleEmailCodeSubmit : handlePasswordSubmit
          }
          className="space-y-4"
        >
          {emailLocked ? (
            <p className="text-sm text-black/55">
              Sign in with the password for this invite, or use a one-time email code.
            </p>
          ) : null}

          <div className="space-y-2">
            <label htmlFor={`${formId}-email`} className={labelClassName}>
              Email address
            </label>
            <input
              id={`${formId}-email`}
              name="email"
              type="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              readOnly={emailLocked}
              aria-readonly={emailLocked || undefined}
              value={identifier}
              onChange={(event) => {
                setIdentifier(event.target.value);
                if (inlineIdentifierError) {
                  setInlineIdentifierError(null);
                }
              }}
              className={`${inputClassName}${emailLocked ? ' bg-black/[0.04] text-black/70' : ''}`}
            />
            {inlineIdentifierError ? (
              <p className="text-sm font-medium text-red-600">{inlineIdentifierError}</p>
            ) : signInIdentifierFieldMessage ? (
              <p className="text-sm font-medium text-red-600">{signInIdentifierFieldMessage}</p>
            ) : null}
          </div>

          {showInlineEmailCodeOnPasswordForm ? (
            <>
              {authMode === 'sign_up' ? (
                <p className="text-sm text-black/55" role="status">
                  We sent a code to{' '}
                  <span className="font-semibold text-ink">{identifier.trim()}</span> to create your
                  account.
                </p>
              ) : null}
              <p className="text-sm">
                <button
                  type="button"
                  className={`${secondaryLinkClassName} font-semibold`}
                  onClick={() => void handleResendCode()}
                  disabled={isSubmitting}
                >
                  Resend code
                </button>
              </p>
              <div className="space-y-2">
                <label htmlFor={`${formId}-inline-code`} className={labelClassName}>
                  Verification code
                </label>
                <input
                  id={`${formId}-inline-code`}
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  autoFocus
                  required
                  value={verificationCode}
                  onChange={(event) => setVerificationCode(event.target.value)}
                  className={inputClassName}
                />
                {errors.fields.code?.message ? (
                  <p className="text-sm font-medium text-red-600">{errors.fields.code.message}</p>
                ) : null}
              </div>
            </>
          ) : (
            <div className="relative">
              <label htmlFor={`${formId}-password`} className={labelClassName}>
                Password
              </label>
              <input
                id={`${formId}-password`}
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={`mt-2 ${inputClassName}`}
              />
              {errors.fields.password?.message || signUpErrors.fields.password?.message ? (
                <p className="mt-2 text-sm font-medium text-red-600">
                  {errors.fields.password?.message ?? signUpErrors.fields.password?.message}
                </p>
              ) : null}
              <button
                type="button"
                className="absolute right-0 top-0 text-sm font-semibold text-coral hover:underline"
                onPointerEnter={() => void loadPasswordResetPanel()}
                onFocus={() => void loadPasswordResetPanel()}
                onClick={() => openPasswordReset('forgot')}
              >
                Forgot password?
              </button>
            </div>
          )}

          <button type="submit" className={primaryButtonClassName} disabled={isBusy}>
            Continue
          </button>

          <p className="text-center text-sm">
            {showInlineEmailCodeOnPasswordForm ? (
              <button
                type="button"
                className={secondaryLinkClassName}
                onClick={switchBackToPasswordFromInlineCode}
              >
                Use email and password instead
              </button>
            ) : (
              <button
                type="button"
                className={secondaryLinkClassName}
                onClick={() => void requestInlineEmailCode()}
              >
                Email me a code instead
              </button>
            )}
          </p>
        </form>
      )}

      {formError ? (
        <p className="mt-4 text-sm font-medium text-red-600" role="alert">
          {formError}
        </p>
      ) : null}

      <div id="clerk-captcha" className="mt-2 empty:hidden" />
    </div>
  );
}

function normalizeEmail(value: string) {
  return value.trim().toLowerCase();
}

function AlternateMethodButton({
  name,
  label,
  mark,
  pending,
  disabled,
  onClick,
}: {
  name: string;
  label: string;
  mark: ReactNode;
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={name}
      aria-busy={pending || undefined}
      className={alternateButtonClassName}
      disabled={disabled}
      onClick={onClick}
    >
      {pending ? <LoadingIcon size={18} className="shrink-0" /> : mark}
      <span className="truncate">{label}</span>
    </button>
  );
}
